import React, { useEffect, useState } from 'react';

import { EditOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { User } from 'types/graphql';

import { useMutation, useQuery } from '@redwoodjs/web';

import CustomModal from 'src/components/CustomModal/CustomModal';
import SubCard from 'src/components/SubCard/SubCard';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { UPDATE_USER_MUTATION } from 'src/graphql/mutations/updateUser.mutation';
import { LOGGED_IN_USER_QUERY } from 'src/graphql/queries/users.query';

import EditEmailForm from '../EditEmailForm';
import EditPhoneNumberForm from '../EditPhoneNumberForm';

const MyDetails = () => {
    const [user, setUser] = useState<User>(null);
    const { data, refetch } = useQuery(LOGGED_IN_USER_QUERY);

    useEffect(() => {
        if (data?.loggedInUser) {
            setUser(data.loggedInUser);
        }
    }, [data]);

    const [editPhone, setEditPhone] = React.useState(false);
    const handleEditPhone = () => {
        setEditPhone(!editPhone);
    };

    const [editEmail, setEditEmail] = React.useState(false);
    const handleEditEmail = () => {
        setEditEmail(!editEmail);
    };

    const handleClose = () => {
        setEditPhone(false);
        setEditEmail(false);
    };

    const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
        onCompleted: () => {
            showSuccessToast('Updated successfully');
            handleClose();
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    const handleUpdateUser = (payload) => {
        updateUser({ variables: { id: user.id, input: payload } });
    };

    const getPartnerName = (hierarchyLevel: string) => {
        switch (hierarchyLevel) {
            case 'NPI':
                return 'NPI';
            case 'PARTNER':
                return 'Partner';
            case 'PARTNER_OF_PARTNER':
                return 'Partner of Partner';
            case 'MERCHANT':
                return 'Merchant';
            default:
                return '';
        }
    };

    return (
        <>
            <SubCard sx={{ mt: 2 }} title="My Details">
                <Typography variant="h6" gutterBottom>
                    Name
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {user?.firstName} {user?.lastName}
                </Typography>

                <Typography variant="h6" gutterBottom>
                    Username
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {user?.username}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Email
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {user?.email}
                        </Typography>
                    </Box>
                    <Button sx={{ width: '90px', height: '40px' }} onClick={handleEditEmail}>
                        <EditOutlined />
                    </Button>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Phone
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {user?.phoneNumber}
                        </Typography>
                    </Box>
                    <Button sx={{ width: '90px', height: '40px' }} onClick={handleEditPhone}>
                        <EditOutlined />
                    </Button>
                </Box>

                <Typography variant="h6" gutterBottom>
                    Hierarchy type
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {getPartnerName(user?.role?.hierarchyLevel)}
                </Typography>

                <Typography variant="h6" gutterBottom>
                    Role
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {user?.role?.name}
                </Typography>
            </SubCard>
            <CustomModal
                open={editPhone}
                handleClose={handleClose}
                title="Edit Phone"
                subTitle="To edit your phone number, please enter your new phone number here."
                isCentered={false}
            >
                <EditPhoneNumberForm
                    phoneNumber={user?.phoneNumber}
                    updateUser={handleUpdateUser}
                    handleClose={handleClose}
                    loading={loading}
                    countryCode={user?.countryCode}
                />
            </CustomModal>
            <CustomModal
                open={editEmail}
                handleClose={handleClose}
                title="Edit Email"
                subTitle="To edit your email, please enter your new email here."
                isCentered={false}
            >
                <EditEmailForm
                    email={user?.email}
                    updateUser={handleUpdateUser}
                    handleClose={handleClose}
                    loading={loading}
                />
            </CustomModal>
        </>
    );
};

export default MyDetails;
